import { useCallback, useEffect, useRef } from "react";

import { Form, Formik } from "formik";
import { Typography, Pane } from "neetoui";
import { Input, ActionBlock } from "neetoui/formik";
import { useTranslation } from "react-i18next";

import { PATH_VALIDATION_SCHEMA } from "./constants";
import "./edit-url.scss";
import {
  getPathname,
  getPrefixFromUrl,
  normalizeEditablePath,
  truncatePrefix,
} from "./utils";

const { Header, Body, Footer } = Pane;

const EditURL = ({
  editUrlProps,
  isOpen,
  onClose,
  url,
  isLoading,
  handleEdit,
}) => {
  const { t } = useTranslation();

  const focusRef = useRef(null);
  const prevIsLoadingRef = useRef(isLoading);

  const prefixText = editUrlProps?.prefix || getPrefixFromUrl(url);

  const observerRef = useRef(
    new ResizeObserver(([entry]) =>
      requestAnimationFrame(() => truncatePrefix(entry.target, prefixText))
    )
  );

  const inputRef = useCallback(inputElement => {
    focusRef.current = inputElement;

    inputElement
      ? observerRef.current.observe(inputElement)
      : observerRef.current.disconnect();
  }, []);

  useEffect(() => {
    if (prevIsLoadingRef.current && !isLoading) onClose();

    prevIsLoadingRef.current = isLoading;
  }, [isLoading]);

  return (
    <Pane
      {...{ isOpen, onClose }}
      data-testid="edit-url-modal"
      initialFocusRef={focusRef}
      size="large"
    >
      <Header>
        <Typography lineHeight="normal" style="h2" weight="semibold">
          {t("neetoMolecules.shareViaLink.editUrl.title")}
        </Typography>
      </Header>
      <Formik
        enableReinitialize
        initialValues={{ path: editUrlProps?.editablePath || getPathname(url) }}
        validationSchema={PATH_VALIDATION_SCHEMA}
        onSubmit={({ path }) => handleEdit(path)}
      >
        {({ isSubmitting, dirty, setFieldValue }) => (
          <Form>
            <Body className="flex flex-col gap-y-6">
              <Input
                className="no-padding w-full flex-grow-0"
                data-testid="edit-url-input"
                label={t("neetoMolecules.shareViaLink.editUrl.inputLabel")}
                maxLength={editUrlProps?.maxLength}
                name="path"
                prefix={prefixText}
                ref={inputRef}
                onChange={event =>
                  setFieldValue(
                    "path",
                    normalizeEditablePath(event.target.value)
                  )
                }
              />
              <Typography
                className="neeto-ui-text-gray-700"
                lineHeight="normal"
                style="body2"
              >
                {t("neetoMolecules.shareViaLink.editUrl.description")}
              </Typography>
            </Body>
            <Footer className="flex items-center gap-x-2">
              <ActionBlock
                className="flex items-center gap-x-2"
                cancelButtonProps={{
                  onClick: onClose,
                  disabled: isSubmitting,
                  "data-testid": "edit-url-cancel-button",
                }}
                submitButtonProps={{
                  disabled: isSubmitting || !dirty,
                  loading: isLoading,
                  "data-testid": "edit-url-confirm-button",
                }}
              />
            </Footer>
          </Form>
        )}
      </Formik>
    </Pane>
  );
};

export default EditURL;
